<template>
  <div class="px-4 py-2 container-fluid">
    <div class="row">
      <div class="col-12">
        <div class="card ficha-buscador mt-4 mb-4">
          <div class="titulo"><h5 class="mb-0">{{ titulo }}</h5></div>
          <div class="dataTable-search" style="top:18px;width:50%;max-width:280px;">
            <input
                v-model="searchInput"
                type="text"
                class="dataTable-input"
                placeholder="Búsqueda rápida..."
                style="width:100%;top:-16px;height:22px;"
                @input="onSearchInput"
            />
          </div>
          <div class="descarga" :class="{ doble: $slots['header-actions'] }">
            <p @click="descargarCSV"><i class="fa fa-download" aria-hidden="true"></i></p>
            <slot name="header-actions" />
          </div>
          <div class="borrados" v-if="borrados">
            <soft-switch id="borrados-switch" name="borrados" @change="cambiarBorrados" :checked="mostrarBorrados"></soft-switch>
          </div>
        </div>
      </div>
    </div>
  </div>

  <div class="px-4 container-fluid server-data-table">
    <div class="row">
      <div class="col-12">
        <div class="card ficha-listado">
          <div class="px-4 pb-0 card-body">
            <div class="dataTable-wrapper">
              <div class="dataTable-container">
                <table class="table table-flush dataTable-table manual">
                  <thead class="thead-light">
                    <tr>
                      <th
                        v-for="col in visibleColumns"
                        :key="col.key"
                        :style="{ cursor: col.sortable ? 'pointer' : 'default' }"
                        @click="col.sortable ? changeSort(col) : null"
                        >
                        <span>{{ col.label }}</span>
                        <span v-if="col.sortable" class="ms-1">
                        <template v-if="sortField === col.key">
                            <span v-if="sortDirection === 'asc'"><i class="fa-solid fa-sort-up"></i></span>
                            <span v-else><i class="fa-solid fa-sort-down"></i></span>
                        </template>
                        <template v-else><i class="fa-solid fa-sort"></i></template>
                        </span>
                      </th>
                    </tr>
                  </thead>

                  <tbody v-if="!loading && rows.length > 0" class="text-sm">
                  <tr v-for="row in rows" :key="row[rowKey] ?? JSON.stringify(row)">
                    <td v-for="col in visibleColumns" :key="col.key">
                    <template v-if="col.slot">
                        <slot :name="col.slot" :row="row" :value="row[col.key]" :column="col" />
                    </template>
                    <template v-else-if="col.click">
                      <span
                        class="clickable-cell"
                        @click="col.click(row, row[col.key], col)"
                      >
                        {{ formatCell(row[col.key], col, row) }}
                      </span>
                    </template>
                    <template v-else>
                      {{ formatCell(row[col.key], col, row) }}
                    </template>
                    </td>
                  </tr>
                  </tbody>

                  <tbody v-else-if="loading">
                    <tr>
                        <td :colspan="visibleColumns.length" class="text-center py-4">
                        Cargando...
                        </td>
                    </tr>
                    </tbody>
                    <tbody v-else>
                    <tr>
                        <td :colspan="visibleColumns.length" class="text-center py-4">
                        No se encontraron registros
                        </td>
                    </tr>
                  </tbody>
                </table>
              </div> 
              <div class="dataTable-bottom">
                <div class="dataTable-info">
                    Mostrando <strong>{{ displayStart }}</strong> a <strong>{{ displayEnd }}</strong> de <strong>{{ total }}</strong> registros
                </div>

                <nav class="dataTable-pagination">
                  <ul class="dataTable-pagination-list">
                    <li class="px-2 text-sm" :disabled="page <= 1 || loading" @click="goToPage(1)">Primera</li>
                    <li class="px-2 text-sm" :disabled="page <= 1 || loading" @click="goToPage(page - 1)">Anterior</li>
                    <li
                      v-for="visiblePage in visiblePages"
                      :key="visiblePage"
                      class="px-2 text-sm"
                      :class="{ active: visiblePage === page }"
                      @click="goToPage(visiblePage)"
                    >
                      {{ visiblePage }}
                    </li>
                    <li class="px-2 text-sm" :disabled="page >= totalPages || loading" @click="goToPage(page + 1)">Siguiente</li>
                    <li class="px-2 text-sm" :disabled="page >= totalPages || loading" @click="goToPage(totalPages)">Última</li>
                  </ul>
                </nav>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import SoftSwitch from "@/components/SoftSwitch.vue";

export default {
  name: "ServerDataTable",
  components: {
    SoftSwitch
  },
  props: {
    columns: {
      type: Array,
      required: true
    },
    fetchData: {
      type: Function,
      required: true
    },
    rowKey: {
      type: String,
      default: "id"
    },
    initialSortField: {
      type: String,
      default: ""
    },
    initialSortDirection: {
      type: String,
      default: "asc",
      validator: (v) => ["asc", "desc"].includes(v)
    },
    extraParams: {
      type: Object,
      default: () => ({})
    },
    debounceMs: {
      type: Number,
      default: 400
    },
    titulo: {
      type: String,
      default: "titulo"
    },
    borrados: {
      type: Boolean,
      default: false
    },
  },

  data() {
    return {
      rows: [],
      total: 0,
      page: 1,
      pageSize: 50,
      sortField: this.initialSortField,
      sortDirection: this.initialSortDirection,
      search: "",
      searchInput: "",
      loading: false,
      exportingCsv: false,
      mostrarBorrados: false,
      debounceTimer: null
    };
  },

  computed: {
    visibleColumns() {
      return this.columns.filter((col) => !(!this.mostrarBorrados && col.indicaBaja === true));
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.total / this.pageSize));
    },
    visiblePages() {
      const start = Math.max(1, this.page - 3);
      const end = Math.min(this.totalPages, this.page + 3);
      const pages = [];

      for (let currentPage = start; currentPage <= end; currentPage += 1) {
        pages.push(currentPage);
      }

      return pages;
    },
    displayStart() {
      if (this.total === 0) return 0;
      return (this.page - 1) * this.pageSize + 1;
    },
    displayEnd() {
      return Math.min(this.page * this.pageSize, this.total);
    }
  },

  watch: {
    extraParams: {
      deep: true,
      handler() {
        this.page = 1;
        this.loadData();
      }
    }
  },

  methods: {
    buildRequestParams(page = this.page, pageSize = this.pageSize) {
      return {
        page,
        pageSize,
        search: this.search,
        sortField: this.sortField,
        sortDirection: this.sortDirection,
        mostrarBorrados: this.mostrarBorrados,
        ...this.extraParams
      };
    },

    normalizeResponse(response, requestedPage = this.page, requestedPageSize = this.pageSize) {
      const rows = Array.isArray(response?.rows) ? response.rows : [];
      const total = Number(response?.total ?? rows.length);
      const page = Number(response?.page ?? requestedPage);
      const pageSize = Number(response?.pageSize ?? response?.perPage ?? requestedPageSize);

      return {
        rows,
        total: Number.isFinite(total) ? total : rows.length,
        page: Number.isFinite(page) && page > 0 ? page : requestedPage,
        pageSize: Number.isFinite(pageSize) && pageSize > 0 ? pageSize : requestedPageSize
      };
    },

    async loadData() {
      this.loading = true;

      try {
        const response = await this.fetchData(this.buildRequestParams());
        const normalized = this.normalizeResponse(response);

        this.rows = normalized.rows;
        this.total = normalized.total;
        this.page = normalized.page;
      } catch (error) {
        console.error("Error cargando tabla:", error);
        this.rows = [];
        this.total = 0;
      } finally {
        this.loading = false;
      }
    },

    onSearchInput() {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => {
        this.search = this.searchInput;
        this.page = 1;
        this.loadData();
      }, this.debounceMs);
    },

    async cambiarBorrados() {
      this.mostrarBorrados=!this.mostrarBorrados; 
      this.page = 1;
      await this.loadData();
    },

    escapeCsvValue(value) {
      const normalizedValue = `${value ?? ""}`.replace(/"/g, '""');
      return `"${normalizedValue}"`;
    },

    getCsvFilename() {
      const baseName = `${this.titulo || "export"}`
        .trim()
        .toLowerCase()
        .replace(/\s+/g, "-")
        .replace(/[^a-z0-9-_]/g, "");

      return `${baseName || "export"}.csv`;
    },

    async descargarCSV() {
      if (this.exportingCsv) {
        return;
      }

      this.exportingCsv = true;

      try {
        const firstResponse = await this.fetchData(this.buildRequestParams(1, 250));
        const firstPage = this.normalizeResponse(firstResponse, 1, 250);
        const totalPages = Math.max(1, Math.ceil(firstPage.total / firstPage.pageSize));
        let allRows = [...firstPage.rows];

        for (let currentPage = 2; currentPage <= totalPages; currentPage += 1) {
          const response = await this.fetchData(this.buildRequestParams(currentPage, firstPage.pageSize));
          const normalized = this.normalizeResponse(response, currentPage, firstPage.pageSize);
          allRows = allRows.concat(normalized.rows);
        }

        const csvHeader = this.visibleColumns
          .map((column) => this.escapeCsvValue(column.label))
          .join(";");

        const csvRows = allRows.map((row) => this.visibleColumns
          .map((column) => this.escapeCsvValue(this.formatCell(row[column.key], column, row)))
          .join(";"));

        const csvContent = [csvHeader, ...csvRows].join("\n");
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const link = document.createElement("a");

        link.href = URL.createObjectURL(blob);
        link.setAttribute("download", this.getCsvFilename());
        link.click();
        URL.revokeObjectURL(link.href);
      } catch (error) {
        console.error("Error exportando CSV:", error);
      } finally {
        this.exportingCsv = false;
      }
    },

    goToPage(newPage) {
      if (newPage < 1 || newPage > this.totalPages || newPage === this.page) return;
      this.page = newPage;
      this.loadData();
    },

    changeSort(col) {
      if (this.sortField === col.key) {
        this.sortDirection = this.sortDirection === "asc" ? "desc" : "asc";
      } else {
        this.sortField = col.key;
        this.sortDirection = "asc";
      }

      this.page = 1;
      this.loadData();
    },

    formatCell(value, col, row) {
      if (typeof col.format === "function") {
        return col.format(value, row);
      }

      if (col.format === "date") {
        if (!value) return "";
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return "";

        const day = `${date.getDate()}`.padStart(2, "0");
        const month = `${date.getMonth() + 1}`.padStart(2, "0");
        const year = date.getFullYear();

        return `${day}/${month}/${year}`;
      }

      if (col.format === "datetime") {
        if (!value) return "";
        return new Intl.DateTimeFormat("es-ES", {
          dateStyle: "short",
          timeStyle: "short"
        }).format(new Date(value));
      }

      if (col.format === "boolean") {
        return value ? "Sí" : "No";
      }

      return value ?? "";
    },

    refresh() {
      this.loadData();
    }
  },

  mounted() {
    this.loadData();
  },

  beforeUnmount() {
    clearTimeout(this.debounceTimer);
  }
};
</script>

<style lang="scss" scoped>
.server-data-table {
  :deep(.clickable-cell) {
    color: var(--bs-analiza-azul);
    cursor: pointer;
    text-decoration: underline;
  }

  :deep(.table-responsive) {
    max-width: 100%;
    overflow-x: auto !important;
    overflow-y: hidden !important;
    -webkit-overflow-scrolling: touch;
  }

  :deep(.dataTable-table) {
    width: 100%;
    min-width: max-content;
  }
}
</style>
